<template>
  <v-expansion-panel>
    <v-expansion-panel-header>
      <v-row no-gutters>
        <v-col cols="10" style="padding:0 inherit" :style="!section.active ? 'font-style:italic;text-decoration:line-through' : ''">{{ section.label }}</v-col>
        <v-col cols="2" style="padding:0 inherit">{{ section.active ? (section.slideCount + ' Slide' + (section.slideCount === 1 ? '' : 's')) : 'Inactive' }}</v-col>
      </v-row>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="section.label"
            label="Section Label"
            :rules="[v => !!v || 'Required', v => v.toLowerCase() !== 'admin' || 'Invalid Section Label']"
            :readonly="!isAdmin"
            @change="(val) => updateSection(val, 'label')"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="section.code"
            :hint="'Link: orientation.covenant.edu/' + section.code"
            :rules="[v => !!v || 'Required', v => v.length < 20 || 'Limited to 20 characters', v => v.toLowerCase() != 'admin' || 'Invalid code']"
            :readonly="!isAdmin"
            label="Short Code (used for web links)"
            persistent-hint
            @change="(val) => updateSection(val, 'code')"></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-list elevation="1">
            <v-subheader>Section Editors</v-subheader>
            <v-list-item v-for="(item, index) in selectedEditors" :key="'editor-' + index">
              <v-list-item-avatar>
                <v-img :src="item.photo"></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title>{{ item.name.last }}, {{ item.name.first }}</v-list-item-title>
                <v-list-item-subtitle>{{ item.location.department }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="isAdmin">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn v-on="on" icon small @click="removeEditor(index)">
                      <v-icon color="error">fal fa-times-circle</v-icon>
                    </v-btn>
                  </template>
                  <span>Remove this editor</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
            <v-list-item v-if="selectedEditors.length === 0">
              <v-list-item-content>
                <v-list-item-title>No Editors Added</v-list-item-title>
                <v-list-item-subtitle>Use the box below to find and add new editors</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <v-autocomplete
            v-if="isAdmin"
            v-model="editorSelection"
            :items="editorOptions"
            :search-input.sync="editorSearch"
            :filter="() => true"
            label="Add Editor"
            item-value="ldap"
            item-text="ldap"
            return-object
            outlined
            @change="addEditor"
            style="margin-top:10px">
            <template v-slot:item="data">
              <template>
                <v-list-item-avatar>
                  <img :src="data.item.photo" style="object-fit:cover">
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.name.last }}, {{ data.item.name.first }}</v-list-item-title>
                  <v-list-item-subtitle>{{ data.item.location.department }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" md="6">
          <v-switch
            v-model="section.active"
            :label="section.active ? 'Section is active' : 'Section is inactive'"
            :readonly="!(isAdmin || inArray(user.username, section.editors))"
            @change="(val) => updateSection(val, 'active')">
            <template v-slot:prepend>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="info">fas fa-question-circle</v-icon>
                </template>
                <span>An active section is one that is currently available for students to complete.<br/>An inactive section is one that will not be available for students to complete.</span>
              </v-tooltip>
            </template>
          </v-switch>
          <v-select
            v-if="index > 0"
            v-model="section.dependentSections"
            :items="sections.slice(0, index)"
            :readonly="!isAdmin"
            label="Dependent Sections"
            item-text="label"
            item-value="_id"
            multiple
            outlined
            chips
            small-chips
            deletable-chips
            @change="(val) => updateSection(val, 'dependentSections')">
            <template v-slot:append-outer>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" color="info">fas fa-question-circle</v-icon>
                </template>
                <div style="max-width:400px">Whether this section should be locked until the selected dependent sections have been completed. The sections must be above this one in the order.</div>
              </v-tooltip>
            </template>
          </v-select>
          <section-actions v-if="isAdmin" :section="section" @add="addAction" @update="updateAction" @remove="removeAction"></section-actions>
        </v-col>
      </v-row>
      <v-toolbar flat>
        <v-btn :to="'/admin/preview/' + section.code" text>Preview Slides</v-btn>
        <v-btn v-if="isAdmin || inArray(user.username, section.editors)" :to="'/admin/slides/' + section.code" text>Slide Manager</v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="isAdmin" :disabled="index === 0" text @click="$emit('move', { index, dir: -1 })">
          <v-icon left>fal fa-arrow-up</v-icon>
          Move Up
        </v-btn>
        <v-btn v-if="isAdmin" :disabled="index === sections.length - 1" text @click="$emit('move', { index, dir: 1 })">
          <v-icon left>fal fa-arrow-down</v-icon>
          Move Down
        </v-btn>
      </v-toolbar>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>
<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import { inArray } from '../../helpers/has'
export default {
  props: {
    section: {
      type: Object,
      required: true
    },
    sections: {
      type: Array,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  components: {
    SectionActions: () => import('./sectionActions')
  },
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const isAdmin = computed(() => root.$store.getters.isAdmin)
    const editorSelection = ref('')
    const editorSearch = ref('')
    const editorOptions = ref([])

    watch(editorSearch, () => {
      let query = {
        person: { $in: [ 'Staff', 'Faculty' ] },
        $limit: 10,
        $sort: { 'name.last': 1, 'name.first': 1 }
      }
      if (typeof (editorSearch.value) === 'string' && editorSearch.value !== '') {
        if (editorSearch.value.search(',') >= 0) {
          let [last, first] = editorSearch.value.split(',')
          query['name.last'] = { $regex: last.trim(), $options: 'i' }
          query['name.first'] = { $regex: first.trim(), $options: 'i' }
        } else if (editorSearch.value.search(' ') >= 0) {
          let [first, last] = editorSearch.value.split(' ')
          query['name.first'] = { $regex: first.trim(), $options: 'i' }
          query['name.last'] = { $regex: last.trim(), $options: 'i' }
        } else {
          query.$or = [
            { 'name.last': { $regex: editorSearch.value, $options: 'i' } },
            { 'name.first': { $regex: editorSearch.value, $options: 'i' } }
          ]
        }
      }
      root.$feathers.service('directory/people').find({ query }).then(({ data }) => {
        editorOptions.value = data
      })
    })

    const selectedEditors = ref([])
    function addEditor (item) {
      if (typeof (item) === 'object' && 'ldap' in item) {
        selectedEditors.value.push(item)
        root.$nextTick(() => {
          editorSearch.value = ''
          editorSelection.value = ''
        })
        service.patch(props.section._id, { $push: { editors: item.ldap } }).then(() => {
          root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Editor has been added.' })
        })
      }
    }
    function removeEditor (index) {
      selectedEditors.value.splice(index, 1)
      let editors = []
      for (let i = 0; i < selectedEditors.value.length; i++) {
        editors.push(selectedEditors.value[i].ldap)
      }
      service.patch(props.section._id, { editors }).then(() => {
        root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Editor has been removed.' })
      })
    }

    onMounted(() => {
      root.$feathers.service('directory/people').find({ query: { ldap: { $in: props.section.editors }, $limit: 50, $sort: { 'name.last': 1, 'name.first': 1 } } }).then(({ data }) => {
        selectedEditors.value = data
      })
    })

    const service = root.$feathers.service('orientation/section')

    function updateSection (value, field) {
      if (field === 'code' || field === 'label') {
        if (value === 'admin') {
          root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'Invalid ' + field + ': "Admin is reserved for the administrative section and cannot be used as a code or label.' })
          return
        } else if (value === 'action') {
          root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'Invalid ' + field + ': "Action" is reserved for student actions and cannot be used as a code or label.' })
          return
        }
        // check to make sure the value of this field does not already exist in the database for a different section
        let query = { [field]: value, _id: { $ne: props.section._id } }
        service.find({ query }).then(({ data }) => {
          if (data.length > 0) {
            root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'Cannot update ' + field + ': the selected value already exists on another section.' })
          } else {
            service.patch(props.section._id, { [field]: value }).then(() => {
              root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: field.substr(0, 1).toUpperCase() + field.substr(1) + ' has been updated.' })
            })
          }
        })
      } else {
        service.patch(props.section._id, { [field]: value }).then(() => {
          root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: field.substr(0, 1).toUpperCase() + field.substr(1) + ' has been updated.' })
        })
      }
    }

    function addAction ({ title, link, openDate, condition }) {
      service.patch(props.section._id, { $push: { actions: { title, link, openDate, condition } } }).then(() => {
        root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Action added.' })
      }).catch((e) => {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Error adding action: ' + e })
      })
    }
    async function updateAction ({ index, obj }) {
      const patch = { [`actions.${index}`]: obj }
      let origLink = obj.link
      let origTitle = obj.title
      if ('actions' in props.section && index in props.section.actions) {
        origLink = props.section.actions[index].link
        origTitle = props.section.actions[index].title
      }
      try {
        await service.patch(props.section._id, patch)
        root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Action updated.' })
      } catch (e) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Error updating action: ' + e })
      }
      // Find anyone who had the previous action link and title and update their action to the new link/title (if either of them changed)
      if (origLink !== obj.link || origTitle !== obj.title) {
        let query = { actions: { $elemMatch: { title: origTitle, link: origLink, $or: [{ done: { $exists: false } }, { done: false }] } }, $limit: 0 }
        let { total } = await root.$feathers.service('orientation/tracker').find({ query })
        query.$limit = 20
        for (let i = 0; i < total; i += 20) {
          query.$skip = i
          let { data } = await root.$feathers.service('orientation/tracker').find({ query })
          for (let j = 0; j < data.length; j++) {
            for (let k = 0; k < data[j].actions.length; k++) {
              if (data[j].actions[k].title === origTitle && data[j].actions[k].link === origLink) {
                await root.$feathers.service('orientation/tracker').patch(data[j]._id, { ['actions.' + k + '.title']: obj.title, ['actions.' + k + '.link']: obj.link })
              }
            }
          }
        }
      }
    }
    function removeAction (index) {
      let actions = []
      for (let i = 0; i < props.section.actions.length; i++) {
        if (i === index) continue
        actions.push({ ...props.section.actions[i] })
      }
      service.patch(props.section._id, { actions }).then(() => {
        root.$store.dispatch('snackbar', { active: true, color: 'success', timeout: 6000, text: 'Action has been removed.' })
      }).catch((e) => {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Error removing action: ' + e })
      })
    }

    return {
      user,
      isAdmin,
      editorSelection,
      editorSearch,
      editorOptions,
      selectedEditors,
      addEditor,
      removeEditor,
      updateSection,
      addAction,
      updateAction,
      removeAction,
      inArray
    }
  }
}
</script>
